import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, LogOut } from "lucide-react";
import { Logo } from "../components/Logo";
import { GoogleButton } from "../components/GoogleButton";
import { api, money } from "../lib/api";
import { startGoogleSignIn } from "../lib/googleAuth";

const STATUS = { awaiting_payment: "Awaiting payment", paid: "Paid — delivering", delivered: "Delivered", cancelled: "Cancelled" };

const OrderRow = ({ o }) => (
  <Link to={o.link} className="flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-[#0f0f16] p-5 transition-colors duration-300 hover:border-[#a259ff]/60" data-testid={`account-order-${o.id}`}>
    <div>
      <p className="font-mono text-xs uppercase tracking-widest text-violet-300">{o.reference}</p>
      <p className="mt-1 font-display text-2xl uppercase text-white">{o.product_name}</p>
      <p className="mt-1 text-sm text-slate-400">{new Date(o.created_at).toLocaleDateString()} · {money(o.amount)}</p>
    </div>
    <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-200" data-testid={`account-order-status-${o.id}`}>
      {STATUS[o.status] ?? o.status} <ArrowUpRight className="h-4 w-4" />
    </span>
  </Link>
);

export default function Account() {
  const [me, setMe] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get("/customer/me")
      .then((r) => {
        setMe(r.data);
        return api.get("/customer/orders");
      })
      .then((r) => setOrders(Array.isArray(r?.data) ? r.data : []))
      .catch(() => setMe(false));
  }, []);

  const logout = async () => {
    await api.post("/customer/logout").catch(() => null);
    setMe(false);
    setOrders([]);
  };

  return (
    <div className="grain grid-lines min-h-screen bg-[#07070b] px-5 py-8 md:px-10">
      <Link to="/" data-testid="account-home-link"><Logo /></Link>
      <div className="mx-auto mt-16 max-w-2xl" data-testid="account-page">
        <h1 className="font-display text-6xl uppercase leading-none text-white">My orders</h1>
        {me === false && (
          <div className="mt-6 max-w-md space-y-4 border border-white/10 bg-[#0b0b11]/90 p-8" data-testid="account-signin">
            <p className="text-slate-300">Sign in with the Google account that uses the same email you checked out with to see all your orders and logins in one place.</p>
            <GoogleButton onClick={() => startGoogleSignIn("/account")} testId="account-google-signin" />
            <Link to="/find-order" className="block text-center font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-white" data-testid="account-email-lookup-link">Or get your order links by email</Link>
          </div>
        )}
        {me && (
          <>
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-y border-white/10 py-4">
              <p className="text-sm text-slate-300" data-testid="account-email">Signed in as <b className="text-white">{me.email}</b></p>
              <button onClick={logout} className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-300 hover:text-white" data-testid="account-logout-button">
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </div>
            <div className="mt-6 space-y-3" data-testid="account-orders">
              {orders.length === 0 && <p className="text-slate-400" data-testid="account-orders-empty">No orders for this email yet. <Link to="/#catalog" className="text-[#e9d5ff] underline">Browse accounts</Link></p>}
              {orders.map((o) => <OrderRow key={o.id} o={o} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
